import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import {
  ISchedule,
  IScheduleOverride,
  IScheduleSlot
} from '@care-portals/api-interfaces';
import { takeWhile } from 'rxjs/operators';
import { SchedulesFacade } from '../../../+store/+schedules/schedules.facade';
import { klona } from 'klona';

interface ITimeSlot {
  sDate: Date;
  eDate: Date;
  s: number;
  e: number;
  error?: string;
}
@Component({
  selector: 'portals-emr-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.less']
})
export class ScheduleComponent implements OnInit, OnDestroy {
  active = true;
  showNoScheduleLinkedError = false;
  @Input() version: number;
  schedule: ISchedule;
  availableProviders = [];
  selectedProvider: string;
  loading$ = this.scheduleFacade.loading$;
  errors = { name: null, startDate: null, endDate: null };
  icsLink = '';
  isICSdirty = false;
  constructor(private readonly scheduleFacade: SchedulesFacade) {}

  ngOnInit(): void {
    this.scheduleFacade.activeSchedule$
      .pipe(takeWhile(() => this.active))
      .subscribe(schedule => {
        if (!schedule?._id) {
          this.showNoScheduleLinkedError = true;
        } else {
          this.showNoScheduleLinkedError = false;
          this.schedule = klona(schedule);
          this.selectedProvider = this.schedule.provider as string;
          this.isICSdirty = false;
          this.icsLink = schedule.icsLink;
        }
      });
    this.scheduleFacade.getSchedule();
  }

  ngOnDestroy() {
    this.active = false;
  }

  save(): boolean {
    if (!this.validate()) {
      return false;
    }
    const schedule: Partial<ISchedule> = {
      ...this.schedule,
      provider: this.selectedProvider,
      icsLink: this.icsLink
    };
    this.scheduleFacade.updateSchedule(schedule._id, schedule);

    return true;
  }
  validate(): boolean {
    let valid = true;

    /* ------------------------ validate required fields ------------------------ */
    if (!this.schedule.label || !this.schedule.label.trim()) {
      this.errors.name = 'error';
      valid = false;
    } else if (!this.schedule.startDate || !this.schedule.endDate) {
      valid = false;
      this.errors.startDate = 'error';
    } else if (!this.schedule.endDate) {
      valid = false;
      this.errors.endDate = 'error';
    } else {
      this.errors = { name: null, startDate: null, endDate: null };
    }
    if (!valid) return valid;

    return valid;
  }

  slotAddedHandler(slot: IScheduleSlot) {
    this.schedule = { ...this.schedule, slots: [...this.schedule.slots, slot] };
  }

  slotUpdatedHandler({
    slot,
    updates
  }: {
    slot: IScheduleSlot;
    updates: Partial<IScheduleSlot>;
  }) {
    const i = this.findSlotIndex(slot);
    this.schedule.slots.splice(i, 1, { ...slot, ...updates });
    this.schedule = klona(this.schedule);
  }

  slotDeletedHandler(slot: IScheduleSlot) {
    const i = this.findSlotIndex(slot);
    this.schedule.slots.splice(i, 1);
    this.schedule = klona(this.schedule);
  }

  overrideAddedHandler(override: IScheduleOverride) {
    this.schedule = {
      ...this.schedule,
      overrides: [...this.schedule.overrides, override]
    };
  }

  overrideUpdatedHandler({
    override,
    updates
  }: {
    override: IScheduleOverride;
    updates: Partial<IScheduleOverride>;
  }) {
    const i = this.findOverrideIndex(override);
    this.schedule.overrides.splice(i, 1, { ...override, ...updates });
    this.schedule = klona(this.schedule);
  }

  overrideDeletedHandler(override: IScheduleOverride) {
    const i = this.findOverrideIndex(override);
    this.schedule.overrides.splice(i, 1);
    this.schedule = klona(this.schedule);
  }

  findSlotIndex(slot: IScheduleSlot) {
    return this.schedule.slots.findIndex(
      x =>
        x.endMinute === slot.endMinute &&
        x.startMinute === slot.startMinute &&
        x.weekDay === slot.weekDay
    );
  }

  findSlot(slot: IScheduleSlot) {
    const i = this.findSlotIndex(slot);
    if (i === -1) return null;
    return this.schedule.slots[i];
  }

  findOverrideIndex(override: IScheduleOverride) {
    return this.schedule.overrides.findIndex(
      x =>
        x.endMinute === override.endMinute &&
        x.startMinute === override.startMinute &&
        new Date(x.date).toDateString() ===
          new Date(override.date).toDateString()
    );
  }

  findOverride(override: IScheduleOverride) {
    const i = this.findOverrideIndex(override);
    if (i === -1) return null;
    return this.schedule.overrides[i];
  }

  sync() {
    this.scheduleFacade.syncSchedule(this.schedule._id);
  }

  onChangeIcsLink(event) {
    this.isICSdirty = true;
    this.icsLink = event;
  }

  get timezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }


}
